// Pure view layer for the Heat Map module — ported from js/renderers-heatmap.js. The
// legacy builds each style's layer as an innerHTML string; here each returns the marks as
// data (rects / polygons / paths + their fill), which HeatMapModule renders as JSX. d3 is
// used only as pure math (contourDensity, geoPath with no projection, the colour
// interpolators) — the same full d3 v7.9 build the legacy's d3.min.js is.

import * as d3 from "d3";
import type { ProtoEvent, ProtoEventSeason } from "@catbento/shared";
import {
  PITCH_L,
  PITCH_W,
  clamp,
  passOf,
  periodLabel,
  matchLabel,
  resolveGameState,
  MINUTE_FILTER,
  shotFilters,
  passFilters,
  type EventContext,
  type EventFilterDef,
} from "../engines/event-map-kit.js";

// Contour first — it's the default style.
export const HEATMAP_STYLES = [
  { key: "contour", label: "Contour" },
  { key: "hex", label: "Hex" },
  { key: "grid", label: "Grid" },
] as const;

export interface HeatmapAction {
  key: string;
  label: string;
  pluralNoun: string;
  /** The data word in the "no data" message (Q-012): "No pass data for …". */
  noun: string;
  /** KDE smoothing, tuned per action from its real density — sparser samples need more
   *  (passes ~11.5k → 8, key passes ~330 → 10, shots ~224 → 14). */
  contourBandwidth: number;
  events: (season: ProtoEventSeason) => ProtoEvent[];
  filter?: (e: ProtoEvent) => boolean;
  getPoint: (e: ProtoEvent) => [number, number];
}

export const HEATMAP_ACTIONS: readonly HeatmapAction[] = [
  { key: "passOrigins", label: "Passes — Origins", pluralNoun: "passes", noun: "pass", contourBandwidth: 8, events: (s) => s.passes || [], getPoint: (p) => [p.x, p.y] },
  // x_end/y_end are already in the rescaled 114×74 space (unlike shot.end_location).
  { key: "passDestinations", label: "Passes — Destinations", pluralNoun: "passes", noun: "pass", contourBandwidth: 8, events: (s) => s.passes || [], getPoint: (p) => [p.x_end, p.y_end] },
  {
    key: "keyPasses",
    label: "Key Passes & Assists",
    pluralNoun: "key passes",
    noun: "key pass",
    contourBandwidth: 10,
    events: (s) => s.passes || [],
    filter: (p) => !!(passOf(p).goal_assist || passOf(p).shot_assist),
    getPoint: (p) => [p.x, p.y],
  },
  { key: "shots", label: "Shots", pluralNoun: "shots", noun: "shot", contourBandwidth: 14, events: (s) => s.shots || [], getPoint: (s) => [s.x, s.y] },
  { key: "all", label: "All Actions", pluralNoun: "actions", noun: "shot / pass", contourBandwidth: 8, events: (s) => (s.shots || []).concat(s.passes || []), getPoint: (e) => [e.x, e.y] },
];

/** The stored action key → its action (unknown → the first, like the legacy `|| [0]`). */
export function resolveHeatmapAction(key: string | null | undefined): HeatmapAction {
  return HEATMAP_ACTIONS.find((a) => a.key === key) || HEATMAP_ACTIONS[0]!;
}

/** The action's events (after its own filter, e.g. key passes only). */
export function actionEvents(action: HeatmapAction, season: ProtoEventSeason): ProtoEvent[] {
  const events = action.events(season);
  return action.filter ? events.filter(action.filter) : events;
}

/** An all-shots or all-passes action gets that type's FULL filter list. "All Actions"
 *  mixes shot and pass rows, and those lists reach into type-specific fields that would
 *  throw on the other type — so it keeps the narrow set that's safe on both. */
export function heatmapFilters(action: HeatmapAction, ctx: EventContext): EventFilterDef[] {
  if (action.key === "shots") return shotFilters(ctx);
  if (action.key === "all") {
    return [
      { key: "half", label: "Half", getValue: (e) => periodLabel(e.period) },
      { key: "opposition", label: "Opposition", getValue: (e) => matchLabel(ctx, e) },
      { key: "gameState", label: "Game State", getValue: (e) => resolveGameState(ctx, e) },
      MINUTE_FILTER,
    ];
  }
  return passFilters(ctx); // passOrigins, passDestinations, keyPasses
}

/** The plotted points: the action's coordinate, real numbers only. */
export function heatmapPoints(action: HeatmapAction, events: ProtoEvent[]): Array<[number, number]> {
  return events
    .map(action.getPoint)
    .filter(([x, y]) => typeof x === "number" && typeof y === "number" && !Number.isNaN(x) && !Number.isNaN(y));
}

/** The stats-bar count: "N of TOTAL" once filters narrow it, else just N. */
export function plottedText(filteredCount: number, rawCount: number): string {
  return rawCount !== filteredCount
    ? `${filteredCount.toLocaleString()} of ${rawCount.toLocaleString()}`
    : `${filteredCount.toLocaleString()}`;
}

// --- Grid -------------------------------------------------------------------------------
// Coarse counts, blurred by an SVG feGaussianBlur on the whole layer. sqrt(count/max) so one
// outlier cell doesn't flatten the gradient (the app's usual perceptual-area reasoning).
export const GRID_CELL_SIZE = 6; // ~19×13 cells over 114×74

export function gridCells(points: Array<[number, number]>): Array<{ x: number; y: number; size: number; fill: string }> {
  const cols = Math.ceil(PITCH_L / GRID_CELL_SIZE);
  const rows = Math.ceil(PITCH_W / GRID_CELL_SIZE);
  const counts = new Array<number>(cols * rows).fill(0);
  for (const [x, y] of points) {
    const cx = clamp(Math.floor(x / GRID_CELL_SIZE), 0, cols - 1);
    const cy = clamp(Math.floor(y / GRID_CELL_SIZE), 0, rows - 1);
    counts[cy * cols + cx]!++;
  }
  const max = Math.max(1, ...counts);
  const cells: Array<{ x: number; y: number; size: number; fill: string }> = [];
  counts.forEach((c, i) => {
    if (!c) return;
    cells.push({
      x: (i % cols) * GRID_CELL_SIZE,
      y: Math.floor(i / cols) * GRID_CELL_SIZE,
      size: GRID_CELL_SIZE,
      fill: d3.interpolateYlOrRd(Math.sqrt(c / max)),
    });
  });
  return cells;
}

// --- Hex ------------------------------------------------------------------------------------
// Hand-rolled pointy-top axial hex binning (d3-hexbin isn't in core d3). HEX_SIZE is
// centre-to-vertex, 1.25 (a quarter of the original 5 — "miles too big").
export const HEX_SIZE = 1.25;

/** Cube-coordinate rounding to the nearest hex centre. */
export function hexRound(q: number, r: number, s: number): [number, number] {
  let rq = Math.round(q), rr = Math.round(r);
  const rs = Math.round(s);
  const qDiff = Math.abs(rq - q), rDiff = Math.abs(rr - r), sDiff = Math.abs(rs - s);
  if (qDiff > rDiff && qDiff > sDiff) rq = -rr - rs;
  else if (rDiff > sDiff) rr = -rq - rs;
  return [rq, rr];
}

export function pointToHex(x: number, y: number, size: number): [number, number] {
  const q = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / size;
  const r = ((2 / 3) * y) / size;
  return hexRound(q, r, -q - r);
}

export function hexToPoint(q: number, r: number, size: number): [number, number] {
  return [size * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r), size * (1.5 * r)];
}

export function hexPolygonPoints(cx: number, cy: number, size: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    pts.push(`${(cx + size * Math.cos(angle)).toFixed(2)},${(cy + size * Math.sin(angle)).toFixed(2)}`);
  }
  return pts.join(" ");
}

/** One polygon per occupied hex, in first-seen order (the legacy Map's insertion order). */
export function hexCells(points: Array<[number, number]>): Array<{ points: string; fill: string }> {
  const bins = new Map<string, number>();
  for (const [x, y] of points) {
    const [q, r] = pointToHex(x, y, HEX_SIZE);
    const key = q + "," + r;
    bins.set(key, (bins.get(key) || 0) + 1);
  }
  const max = Math.max(1, ...bins.values());
  const polys: Array<{ points: string; fill: string }> = [];
  bins.forEach((count, key) => {
    const [q, r] = key.split(",").map(Number) as [number, number];
    const [cx, cy] = hexToPoint(q, r, HEX_SIZE);
    // A centre just off the pitch still overlaps it — only skip once a whole hex-width out.
    if (cx < -HEX_SIZE * 2 || cx > PITCH_L + HEX_SIZE * 2 || cy < -HEX_SIZE * 2 || cy > PITCH_W + HEX_SIZE * 2) return;
    // 0.05–0.95, not 0–1: the coldest bin stays dark purple, not black on the dark module.
    const t = 0.05 + Math.sqrt(count / max) * 0.9;
    polys.push({ points: hexPolygonPoints(cx, cy, HEX_SIZE * 0.96), fill: d3.interpolateInferno(t) });
  });
  return polys;
}

// --- Contour -------------------------------------------------------------------------------
// A 2D KDE computed directly in pitch units (.size([114, 74])), drawn with geoPath() and no
// projection (identity — planar, not geographic), so no coordinate conversion either way.
export function contourPaths(points: Array<[number, number]>, bandwidth: number): Array<{ d: string; fill: string }> {
  if (points.length < 3) return [];
  const density = d3
    .contourDensity<[number, number]>()
    .x((d) => d[0])
    .y((d) => d[1])
    .size([PITCH_L, PITCH_W])
    .bandwidth(bandwidth)
    .thresholds(10)(points);
  if (!density.length) return [];
  const maxValue = Math.max(...density.map((c) => c.value));
  const path = d3.geoPath();
  // String(), as the legacy template literal does — no fallback for a (never-seen) null path.
  return density.map((c) => ({ d: String(path(c)), fill: d3.interpolateViridis(c.value / maxValue) }));
}
